import React, { useRef, useState, useCallback, useMemo } from 'react';
import { Col } from 'react-bootstrap';
import FeatureToggleUtils from 'FeatureToggleUtils';
import { CaptionsList } from '@bizzabo/components/Captions';
import { VirtualSessionIcon } from '@bizzabo/icons';
import PlayIconFilledIcon from '@bizzabo/icons/dist/PlayIconFilledIcon';
import { MutedPreviewOverlay } from '@bizzabo/broadcast';
import { normalizeCaptions } from '../../../../../utils/normalizeCaptionsUtil';
import { getTimeFromSessionStartInSeconds } from '../../sessionBroadcastUtils';
import { VolumeMuteOutlineIcon } from '@bizzabo/icons';
import {
    initialPlayerConfigs,
    clsSimuliveButton,
    getVersionToScriptUrls,
    buildVideoUrls,
    createPlayVideoWithSessionOffset
} from './utils';
import PrerecordedPlayerWrapper from './PrerecordedPlayerWrapper';

const PrerecordedVideo = ({
    timeZone,
    recordUrl: rawRecordUrl,
    captions = [],
    isScheduledStartTimeHasCome,
    availableHls: hlsEnabled = false,
    isMobileApp = false,
    overridePlayerConfigs = {},
    userId,
    userEmail,
    captionsEnabled,
    turnOnCaptionsForAttendees,
    session = {}
}) => {
    const { id: sessionId, startDate, startMinute, title: sessionTitle } = session;
    const captionTracks = normalizeCaptions(captions);
    const showCaptions = captionsEnabled && captionTracks?.length;

    const versionToScriptUrls = getVersionToScriptUrls();
    const bizzaboAgnosticPlayer = FeatureToggleUtils.getFeatureValue('bizzabo-agnostic-player');
    const playerRef = useRef(null);

    const [showJoinBroadcastButton, setShowJoinBroadcastButton] = useState(false);

    const currentTime = useMemo(() => (
        getTimeFromSessionStartInSeconds({ startDate, startMinute, eventTimeZone: timeZone })
    ), []);

    const playVideoWithSessionOffset = useMemo(() => (
        createPlayVideoWithSessionOffset(playerRef, startDate, startMinute, timeZone)
    ), []);

    const joinToSimulive = () => {
        setShowJoinBroadcastButton(false);
        if (!playerRef.current) return;
        playVideoWithSessionOffset();
    };

    const { recordUrls, shouldLoadHls } = useMemo(() => buildVideoUrls(rawRecordUrl, hlsEnabled), [rawRecordUrl, hlsEnabled]);

    const autoplayFailedCallback = useCallback(() => {
        setShowJoinBroadcastButton(true);
    }, []);

    const playerConfigs = { ...initialPlayerConfigs, ...overridePlayerConfigs, captions: { active: captionsEnabled && turnOnCaptionsForAttendees } };

    const videoInitProps = {
        videoElRef: playerRef,
        recordUrls,
        hlsEnabled: shouldLoadHls,
        playerConfigs,
        autoplayFailedCallback,
        setters: {
            currentTime
        },
        MutedOverlayComponent: <MutedPreviewOverlay IconComponent={VolumeMuteOutlineIcon} />,
        versionToScriptUrls,
        sessionId,
        userId,
        sessionTitle,
        userEmail,
        autoplay: isMobileApp ? 'mute' : 'any',
        captionsEnabled,
        showCaptionsByDefault: captionsEnabled ? turnOnCaptionsForAttendees : false
    };

    if (isMobileApp) {
        return (
            <div className="prerecorded-video" >
                {showJoinBroadcastButton && (
                    <div className="prerecorded-video-play-icon">
                        <PlayIconFilledIcon fontSize='89px' fill='#fff' onClick={joinToSimulive} />
                    </div>
                )}
                <PrerecordedPlayerWrapper
                    isMuxPlayer={false}
                    videoInitProps={videoInitProps}
                    captions={showCaptions ? <CaptionsList captions={captionTracks} type="native" /> : null}
                    session={session}
                />
            </div>
        );
    }

    return (
        <>
            {showJoinBroadcastButton && (
                <button className={clsSimuliveButton} onClick={joinToSimulive}>
                    <VirtualSessionIcon className='broadcast-icon-btn' fill="#ffffff" fontSize="20px" />
                    <span>join broadcast</span>
                </button>
            )}
            <Col className="prerecorded-video">
                {isScheduledStartTimeHasCome && (
                    <PrerecordedPlayerWrapper
                        isMuxPlayer={bizzaboAgnosticPlayer === 'mux' && !captionTracks?.length}
                        videoInitProps={videoInitProps}
                        captions={captionTracks?.length ? <CaptionsList captions={captionTracks} type="native" /> : null}
                        session={session}
                    />
                )}
            </Col>
        </>
    );
};

export default PrerecordedVideo;

